import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Filter, MoreVertical } from 'lucide-react';
import DeleteConfirmModal from './DeleteConfirmModal';
import Card from '../UI/Card';
import Badge from '../UI/Badge';
import Button from '../UI/Button';
import MultiSelectDropdown from '../UI/MultiSelectDropdown';
import StrategyToolsDropdown from './StrategyToolsDropdown';
import NotificationPrefsModal from './NotificationPrefsModal';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '../UI/dropdown-menu';
import { strategiesAPI } from '../../services/api';
import { StaggerContainer, AnimatedItem } from '@/components/motion';

const Sep = () => <span className="text-border/50">|</span>;

function DesktopStrategyManager({
  strategies,
  filteredStrategies,
  statusFilters,
  setStatusFilters,
  statusCounts,
  actionLoading,
  selectedStrategyId,
  setSelectedStrategyId,
  getParams,
  handleAction,
  handleStopClick,
  handleArchiveClick,
  handleEmergencyStop,
  handleImport,
  loadStrategies,
  navigate,
}) {
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [notifStrategyId, setNotifStrategyId] = useState(null);

  const filterOptions = [
    { value: 'running', label: `Running (${statusCounts.running})` },
    { value: 'stopped', label: `Stopped (${statusCounts.stopped})` },
    { value: 'paused', label: `Paused (${statusCounts.paused})` },
    { value: 'error', label: `Error (${statusCounts.error})` },
    { value: 'archived', label: `Archived (${statusCounts.archived})` },
  ];
  return (
    <>
      {/* Strategy Detail Panel */}
      {(() => {
        const sel = filteredStrategies.find((s) => s.id === selectedStrategyId);
        if (!sel) {
          return (
            <StaggerContainer className="grid grid-cols-1 md:grid-cols-4 gap-6">
              {[
                { label: 'Total', value: statusCounts.all, color: 'text-foreground' },
                { label: 'Running', value: statusCounts.running, color: 'text-green-500' },
                { label: 'Stopped', value: statusCounts.stopped, color: 'text-muted-foreground/70' },
                { label: 'Paused', value: statusCounts.paused, color: 'text-yellow-500' },
              ].map((s) => (
                <AnimatedItem key={s.label}>
                  <Card>
                    <div className="text-center">
                      <p className="text-sm text-muted-foreground mb-1">{s.label}</p>
                      <p className={`text-3xl font-bold ${s.color}`}>{s.value}</p>
                    </div>
                  </Card>
                </AnimatedItem>
              ))}
            </StaggerContainer>
          );
        }

        const sp = getParams(sel);
        const statusVariant = { running: 'success', stopped: 'neutral', paused: 'warning', error: 'danger', archived: 'neutral' }[sel.status] || 'neutral';

        const skipKeys = new Set(['assetClass', 'symbol', 'sizingMode']);
        const paramEntries = Object.entries(sp)
          .filter(([k, v]) => !skipKeys.has(k) && v != null && v !== '' && typeof v !== 'object')
          .map(([k, v]) => {
            const label = k.replace(/([A-Z])/g, ' $1').replace(/^./, (c) => c.toUpperCase());
            let display = v;
            if (typeof v === 'number') display = Number.isInteger(v) ? v : v.toFixed(2);
            if (typeof v === 'boolean') display = v ? 'Yes' : 'No';
            return [label, String(display)];
          });

        return (
          <Card className="px-4 py-3">
            <div className="flex items-center gap-3">
              <h2 className="text-lg font-bold text-foreground">{sel.name}</h2>
              <Badge variant={statusVariant}>{sel.status}</Badge>
              <Badge variant={sel.trading_mode === 'paper' ? 'info' : 'warning'}>
                {sel.trading_mode === 'paper' ? 'PAPER' : 'LIVE'}
              </Badge>
              <Badge variant={sp.assetClass === 'crypto' ? 'warning' : 'info'}>
                {sp.assetClass ? sp.assetClass.charAt(0).toUpperCase() + sp.assetClass.slice(1) : 'Stocks'}
              </Badge>
              <span className="ml-auto text-2xl font-black tracking-tight text-primary">{sp.symbol || '—'}</span>
            </div>

            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 pt-2 border-t border-white/5 text-xs text-muted-foreground">
              <span><span className="text-muted-foreground/60">Type</span> <span className="font-semibold text-foreground capitalize">{sel.type?.replace('_', ' ')}</span></span>
              <Sep />
              <span><span className="text-muted-foreground/60">Provider</span> <span className="font-semibold text-foreground">{sel.provider}</span></span>
              {sel.tick_interval_seconds && (
                <>
                  <Sep />
                  <span><span className="text-muted-foreground/60">Interval</span> <span className="font-semibold text-foreground">{sel.tick_interval_seconds}s</span></span>
                </>
              )}
              {sel.last_tick_at && (
                <>
                  <Sep />
                  <span><span className="text-muted-foreground/60">Last Tick</span> <span className="font-semibold text-foreground">{new Date(sel.last_tick_at).toLocaleTimeString()}</span></span>
                </>
              )}
              {sel.executor_enabled != null && (
                <>
                  <Sep />
                  <span><span className="text-muted-foreground/60">Executor</span> <span className={`font-semibold ${sel.executor_enabled ? 'text-green-400' : 'text-muted-foreground'}`}>{sel.executor_enabled ? 'ON' : 'OFF'}</span></span>
                </>
              )}
              <Sep />
              <span><span className="text-muted-foreground/60">Created</span> <span className="font-semibold text-foreground">{new Date(sel.created_at).toLocaleDateString()}</span></span>
              {paramEntries.length > 0 && (
                <>
                  <Sep />
                  {paramEntries.map(([label, value]) => (
                    <span key={label} className="mr-3">
                      <span className="text-muted-foreground/60">{label}</span>{' '}
                      <span className="font-semibold text-foreground">{value}</span>
                    </span>
                  ))}
                </>
              )}
            </div>
          </Card>
        );
      })()}

      {/* Strategies Table */}
      <Card className="flex-1">
        {/* Filter Bar */}
        <div className="flex items-center flex-wrap gap-2 mb-4">
          <MultiSelectDropdown
            label="Filter"
            icon={Filter}
            options={filterOptions}
            selected={statusFilters}
            onChange={setStatusFilters}
          />
          <div className="ml-auto flex items-center gap-2">
            <StrategyToolsDropdown
              strategy={filteredStrategies.find((s) => s.id === selectedStrategyId)}
              mode="manager"
              onImport={handleImport}
              onDuplicate={() => loadStrategies()}
              onNotifications={() => {
                const sel = filteredStrategies.find((s) => s.id === selectedStrategyId);
                if (sel) setNotifStrategyId(sel.id);
              }}
              showLabel
            />
            <button
              onClick={handleEmergencyStop}
              className="px-3 py-1.5 rounded-lg text-xs font-medium bg-red-900/40 border border-red-700 text-red-400 hover:bg-red-900/60 transition-colors"
            >
              <span className="sm:hidden">⏹ Stop All</span><span className="hidden sm:inline">⏹ Emergency Stop All</span>
            </button>
          </div>
        </div>
        {filteredStrategies.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full table-fixed divide-y divide-white/8">
              <thead>
                <tr>
                  <th className="px-2 sm:px-4 py-2.5 sm:py-3 text-center text-xs font-medium text-muted-foreground uppercase tracking-wider">Strategy Name</th>
                  <th className="px-2 sm:px-4 py-2.5 sm:py-3 text-center text-xs font-medium text-muted-foreground uppercase tracking-wider">Ticker</th>
                  <th className="px-2 sm:px-4 py-2.5 sm:py-3 text-center text-xs font-medium text-muted-foreground uppercase tracking-wider hidden sm:table-cell">Asset Type</th>
                  <th className="px-2 sm:px-4 py-2.5 sm:py-3 text-center text-xs font-medium text-muted-foreground uppercase tracking-wider">Status</th>
                  <th className="px-2 sm:px-4 py-2.5 sm:py-3 text-center text-xs font-medium text-muted-foreground uppercase tracking-wider hidden sm:table-cell">Mode</th>
                  <th className="px-2 sm:px-4 py-2.5 sm:py-3 text-center text-xs font-medium text-muted-foreground uppercase tracking-wider hidden md:table-cell">Created</th>
                  <th className="px-2 sm:px-4 py-2.5 sm:py-3 text-center text-xs font-medium text-muted-foreground uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/6">
                {filteredStrategies.map((row) => {
                  const isLoading = actionLoading[row.id];
                  const isRunning = row.status === 'running';
                  const isStopped = row.status === 'stopped';
                  const isPaused = row.status === 'paused';
                  const isError = row.status === 'error';
                  const isArchived = row.status === 'archived';
                  const params = getParams(row);

                  return (
                    <tr
                      key={row.id}
                      onClick={() => setSelectedStrategyId(row.id)}
                      className={`hover:bg-white/[0.04] transition-colors cursor-pointer ${
                        selectedStrategyId === row.id ? 'bg-primary/5 ring-1 ring-inset ring-primary/20' : ''
                      }`}
                    >
                      <td className="px-2 sm:px-4 py-2.5 sm:py-3 whitespace-nowrap text-sm text-muted-foreground text-center">
                        <div>
                          <p className="font-semibold text-foreground">{row.name}</p>
                          <p className="text-xs text-muted-foreground/70 capitalize">{row.type?.replace('_', ' ')}</p>
                        </div>
                      </td>
                      <td className="px-2 sm:px-4 py-2.5 sm:py-3 whitespace-nowrap text-sm text-muted-foreground text-center">
                        <span className="font-mono text-foreground font-semibold">{params.symbol || '—'}</span>
                      </td>
                      <td className="px-2 sm:px-4 py-2.5 sm:py-3 whitespace-nowrap text-sm text-muted-foreground text-center hidden sm:table-cell">
                        <Badge variant={params.assetClass === 'crypto' ? 'warning' : 'info'}>
                          {params.assetClass ? params.assetClass.charAt(0).toUpperCase() + params.assetClass.slice(1) : '—'}
                        </Badge>
                      </td>
                      <td className="px-2 sm:px-4 py-2.5 sm:py-3 whitespace-nowrap text-sm text-muted-foreground text-center">
                        <Badge variant={{ running: 'success', stopped: 'neutral', paused: 'warning', error: 'danger', archived: 'neutral' }[row.status] || 'neutral'}>
                          {row.status?.toUpperCase()}
                        </Badge>
                      </td>
                      <td className="px-2 sm:px-4 py-2.5 sm:py-3 whitespace-nowrap text-sm text-muted-foreground text-center hidden sm:table-cell">
                        <div className="flex items-center justify-center">
                          <Badge variant={row.trading_mode === 'paper' ? 'info' : 'warning'}>
                            {row.trading_mode === 'paper' ? 'PAPER' : 'LIVE'}
                          </Badge>
                        </div>
                      </td>
                      <td className="px-2 sm:px-4 py-2.5 sm:py-3 whitespace-nowrap text-sm text-muted-foreground text-center hidden md:table-cell">
                        {new Date(row.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-2 sm:px-4 py-2.5 sm:py-3 whitespace-nowrap text-sm text-muted-foreground text-center">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button
                              type="button"
                              onClick={(e) => e.stopPropagation()}
                              className="inline-flex items-center justify-center h-8 w-8 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent/10 transition-colors"
                            >
                              <MoreVertical className="h-4 w-4" />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="min-w-[160px]" onClick={(e) => e.stopPropagation()}>
                            <DropdownMenuItem onClick={() => navigate(`/strategies/trading-floor/${row.id}`)}>
                              Trading Floor
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => navigate(`/analysis/backtesting?strategy=${row.id}`)}>
                              Backtest
                            </DropdownMenuItem>

                            {!isArchived && (
                              <>
                                <DropdownMenuSeparator />

                                {(isStopped || isPaused || isError) && (
                                  <DropdownMenuItem
                                    disabled={!!isLoading}
                                    onClick={() => handleAction(row.id, isPaused ? strategiesAPI.resume : strategiesAPI.start, isPaused ? 'resume' : 'start')}
                                    className="text-green-400 focus:text-green-300"
                                  >
                                    {isPaused ? 'Resume' : 'Start'}
                                  </DropdownMenuItem>
                                )}

                                {isRunning && (
                                  <DropdownMenuItem
                                    disabled={!!isLoading}
                                    onClick={() => handleAction(row.id, strategiesAPI.pause, 'pause')}
                                    className="text-yellow-400 focus:text-yellow-300"
                                  >
                                    Pause
                                  </DropdownMenuItem>
                                )}

                                {(isRunning || isPaused || isError) && (
                                  <DropdownMenuItem
                                    disabled={!!isLoading}
                                    onClick={() => handleStopClick(row)}
                                    className="text-red-400 focus:text-red-300"
                                  >
                                    Stop
                                  </DropdownMenuItem>
                                )}

                                <DropdownMenuSeparator />

                                {!isRunning && (
                                  <DropdownMenuItem onClick={() => navigate(`/strategies/builder/${row.id}`)}>
                                    Edit
                                  </DropdownMenuItem>
                                )}

                                {(isStopped || isError) && (
                                  <DropdownMenuItem
                                    disabled={!!isLoading}
                                    onClick={() => handleArchiveClick(row)}
                                  >
                                    Archive
                                  </DropdownMenuItem>
                                )}
                              </>
                            )}

                            {isArchived && (
                              <>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                  onClick={() => handleAction(row.id, strategiesAPI.unarchive, 'unarchive')}
                                  disabled={!!isLoading}
                                  className="text-green-400 focus:text-green-300"
                                >
                                  Unarchive
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onClick={() => setDeleteTarget(row)}
                                  disabled={!!isLoading}
                                  className="text-red-400 focus:text-red-300"
                                >
                                  Delete Permanently
                                </DropdownMenuItem>
                              </>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : strategies.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">🎯</div>
            <p className="text-muted-foreground mb-4">No strategies yet</p>
            <Link to="/strategies/builder">
              <Button>Create Your First Strategy</Button>
            </Link>
          </div>
        ) : (
          <div className="text-center py-12">
            <p className="text-muted-foreground">No matching strategies</p>
          </div>
        )}
      </Card>

      <DeleteConfirmModal
        strategy={deleteTarget}
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={async () => {
          await handleAction(deleteTarget.id, strategiesAPI.permanentDelete, 'delete');
          setDeleteTarget(null);
        }}
      />
      <NotificationPrefsModal
        open={!!notifStrategyId}
        onClose={() => setNotifStrategyId(null)}
        strategyId={notifStrategyId}
      />
    </>
  );
}

export default DesktopStrategyManager;
